import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { startEvaluation, getEvaluationStatus } from '../api/api';

const EvaluationPage = () => {
  const { batchId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const skipAutoStart = Boolean(location.state?.skipAutoStart);
  const [status, setStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [evaluationStarted, setEvaluationStarted] = useState(false);
  
  // Auto-start evaluation when the page loads (skip if re-assessment was already triggered)
  useEffect(() => {
    const autoStartEvaluation = async () => {
      if (evaluationStarted) {
        return;
      }

      if (skipAutoStart) {
        setEvaluationStarted(true);
        return;
      }

      try {
        await startEvaluation(batchId);
        setEvaluationStarted(true);
      } catch (err) {
        setError(err.detail || 'Error auto-starting evaluation. You can try the manual start button below.');
      }
    };

    autoStartEvaluation();
  }, [batchId, evaluationStarted, skipAutoStart]);

  // Start evaluation
  const handleStartEvaluation = async () => {
    try {
      setIsLoading(true);
      await startEvaluation(batchId);
      setEvaluationStarted(true);
    } catch (err) {
      setError(err.detail || 'Error starting evaluation. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Poll evaluation status
  useEffect(() => {
    let interval;

    const fetchStatus = async () => {
      try {
        const data = await getEvaluationStatus(batchId);
        setStatus(data);

        // If evaluation is complete, navigate to report page
        if (data.is_complete) {
          clearInterval(interval);
          setTimeout(() => {
            navigate(`/report/${batchId}`);
          }, 2000);
        }
      } catch (err) {
        setError(err.detail || 'Error fetching evaluation status.');
        clearInterval(interval);
      } finally {
        setIsLoading(false);
      }
    };

    // Only poll if evaluation has been started
    if (evaluationStarted) {
      fetchStatus();
      interval = setInterval(fetchStatus, 5000); // Poll every 5 seconds
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [batchId, navigate, evaluationStarted]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white shadow-md rounded-lg p-6 mb-8">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">CV Evaluation</h1>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {!evaluationStarted ? (
          <div className="text-center py-12">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 text-primary-600 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Ready to Evaluate CVs</h2>
            <p className="text-gray-600 mb-6">
              We'll analyze each CV against your specified job requirements and scoring criteria.
              <br />
              This process may take a few minutes depending on the number of CVs.
            </p>
            <button
              onClick={handleStartEvaluation}
              disabled={isLoading}
              className={`bg-primary-600 text-white py-2 px-6 rounded-md font-medium hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                isLoading ? 'opacity-75 cursor-not-allowed' : ''
              }`}
            >
              {isLoading ? 'Starting...' : 'Start Evaluation Process'}
            </button>
          </div>
        ) : (
          <div className="text-center py-8">
            {status ? (
              <>
                <h2 className="text-xl font-semibold text-gray-700 mb-4">
                  Evaluating {status.total_candidates} CVs for {status.job_title}
                </h2>

                <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                  <div
                    className="bg-primary-600 h-4 rounded-full transition-all duration-500 ease-in-out"
                    style={{ width: `${status.progress_percentage}%` }}
                  ></div>
                </div>

                <p className="text-gray-600 mb-2">
                  {status.evaluated_candidates} of {status.total_candidates} CVs evaluated
                </p>
                <p className="text-gray-500 text-sm">
                  {status.is_complete
                    ? 'Evaluation complete! Redirecting to results...'
                    : 'This may take a few minutes. Please do not close this page.'}
                </p>
              </>
            ) : (
              <div className="flex justify-center items-center">
                <svg
                  className="animate-spin h-8 w-8 text-primary-600"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                <span className="ml-3 text-gray-600">Loading evaluation status...</span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EvaluationPage;
